import {
  useCallback,
  useContext,
  useEffect,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { LeftOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useNavigate } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { SIDER_TABS } from "../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../utils/NavigationUtils";
import { useAffiliateDeepLinkBaseUrl } from "../../../hooks/useAffiliateDeepLinkBaseUrl";
import useCurrencySymbol from "../../../hooks/useCurrencySymbol";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import ContactInformation from "./ContactInformation/ContactInformation";
import {
  isContactInfoComplete,
  type ContactInformationFormValues,
} from "./ContactInformation/contactInformationData";
import AffiliatePamAccount from "./AffiliatePamAccount/AffiliatePamAccount";
import {
  AFFILIATE_PAM_ACCOUNT_INITIAL_VALUES,
  isPamAccountValid,
  type AffiliatePamAccountFormValues,
} from "./AffiliatePamAccount/affiliatePamAccountData";
import PromoCodes from "./PromoCodes/PromoCodes";
import type { CreatePromoCodeFormValues } from "./PromoCodes/CreatePromoCodeModal/createPromoCodeModalData";
import {
  buildAffiliateDeepLinkUrl,
  buildPromoCodeSlug,
} from "./PromoCodes/promoCodesData";
import Payment, { isPaymentValid } from "./Payment/Payment";
import type {
  CpaActionType,
  PaymentStepValue,
  RevenueSource,
} from "./Payment/Payment";
import Contract from "./Contract/Contract";
import Confirmation from "./Confirmation/Confirmation";

import WizardStepper from "../../shared/WizardStepper/WizardStepper";

import { useCreateAffiliate } from "../../../queries/affiliates/useCreateAffiliate";
import { useAffiliateContractUploadUrl } from "../../../queries/affiliates/useAffiliateContractUploadUrl";
import {
  mapContactToPayload,
  mapPaymentToPayload,
  mapPortalViewConfigToPayload,
  mapPromoCodesToPayload,
} from "../../../queries/affiliates/affiliateMappers";
import type {
  CreateAffiliateRequest,
  CreateAffiliateResponseData,
} from "../../../types/affiliates";

import styles from "./NewAffiliate.module.css";

type AppContextValue = {
  setActiveSidebarTab: (tabName: string, dropdownOption?: string | false) => void;
};

type NewAffiliateStep =
  | "contact"
  | "pamAccount"
  | "promoCodes"
  | "payment"
  | "contract"
  | "confirmation";

const NEW_AFFILIATE_STEPS: ReadonlyArray<{
  id: NewAffiliateStep;
  label: string;
}> = [
  { id: "contact", label: AppStrings.NEW_AFFILIATE_CONTACT_INFORMATION },
  { id: "pamAccount", label: AppStrings.NEW_AFFILIATE_PAM_ACCOUNT },
  { id: "promoCodes", label: AppStrings.NEW_AFFILIATE_PROMO_CODES },
  { id: "payment", label: AppStrings.AFFILIATE_PAYMENT },
  { id: "contract", label: AppStrings.AFFILIATE_CONTRACT },
];

const EMPTY_VALUE = AppStrings.PAM_EMPTY_VALUE;

const formatCpaActionType = (t?: CpaActionType): string => {
  if (t === "first_time_deposits") return AppStrings.FIRST_TIME_DEPOSITS;
  if (t === "user_signups") return AppStrings.USER_SIGNUPS;
  return EMPTY_VALUE;
};

const formatRevenueSource = (s?: RevenueSource): string => {
  if (s === "sportsbook_casino")
    return AppStrings.SPORTSBOOK_CASINO_REVENUE;
  if (s === "sportsbook") return AppStrings.SPORTSBOOK_REVENUE;
  if (s === "casino") return AppStrings.CASINO_REVENUE;
  return EMPTY_VALUE;
};

const formatAmount = (
  raw: number | string | null | undefined,
  currencySymbol: string,
): string => {
  if (raw === null || raw === undefined || raw === "") return EMPTY_VALUE;
  return `${currencySymbol}${raw}`;
};

const formatPct = (raw?: number | string | null): string =>
  raw === null || raw === undefined || raw === "" ? EMPTY_VALUE : `${raw}%`;

const NewAffiliate: FC = () => {
  usePageTitle(AppStrings.NEW_AFFILIATE);

  const navigate = useNavigate();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;
  const currencySymbol = useCurrencySymbol() ?? "$";
  const [activeStep, setActiveStep] = useState<NewAffiliateStep>("contact");
  const [completedSteps, setCompletedSteps] = useState<Set<NewAffiliateStep>>(
    new Set(),
  );
  const [contactInfo, setContactInfo] = useState<
    Partial<ContactInformationFormValues>
  >({});
  const [pamAccount, setPamAccount] = useState<AffiliatePamAccountFormValues>(
    () => ({ ...AFFILIATE_PAM_ACCOUNT_INITIAL_VALUES }),
  );
  const [promoCodes, setPromoCodes] = useState<CreatePromoCodeFormValues[]>(
    [],
  );
  const [payment, setPayment] = useState<Partial<PaymentStepValue>>({});
  const [contractFile, setContractFile] = useState<File | null>(null);
  const [confirmation, setConfirmation] =
    useState<CreateAffiliateResponseData | null>(null);

  const createAffiliate = useCreateAffiliate();
  const presignContract = useAffiliateContractUploadUrl();
  const isSubmitting = createAffiliate.isPending || presignContract.isPending;

  // Mirror what the Create Promo Code modal shows so summary + modal stay
  // in sync. Empty hides the URL line.
  const deepLinkBaseUrl = useAffiliateDeepLinkBaseUrl();

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.PROMOTIONS, "AFFILIATES");
  }, [setActiveSidebarTab]);

  const handleBack = useCallback(() => {
    navigate(NAVIGATION_ROUTES.MARKETING_AFFILIATES);
  }, [navigate]);

  const handleCreatePromoCode = useCallback(
    (values: CreatePromoCodeFormValues) => {
      setPromoCodes((current) => [...current, values]);
    },
    [],
  );

  const handleEditPromoCode = useCallback(
    (index: number, values: CreatePromoCodeFormValues) => {
      setPromoCodes((current) => {
        if (index < 0 || index >= current.length) return current;
        const next = current.slice();
        next[index] = values;
        return next;
      });
    },
    [],
  );

  const markStepCompleted = useCallback((step: NewAffiliateStep) => {
    setCompletedSteps((current) => {
      if (current.has(step)) return current;
      const next = new Set(current);
      next.add(step);
      return next;
    });
  }, []);

  // forward nav rule: clicking a stepper button only moves you to a step you've
  // already submitted. To advance into a new step, the user must hit Continue.
  const handleStepClick = useCallback(
    (id: string) => {
      if (activeStep === "confirmation") return;
      if (id === activeStep) return;
      if (!completedSteps.has(id as NewAffiliateStep)) return;
      setActiveStep(id as NewAffiliateStep);
    },
    [activeStep, completedSteps],
  );

  const handleContactInfoSubmit = useCallback(() => {
    markStepCompleted("contact");
    setActiveStep("pamAccount");
  }, [markStepCompleted]);

  const handlePamAccountContinue = useCallback(
    (values: AffiliatePamAccountFormValues) => {
      setPamAccount(values);
      markStepCompleted("pamAccount");
      setActiveStep("promoCodes");
    },
    [markStepCompleted],
  );

  const handlePromoCodesContinue = useCallback(() => {
    markStepCompleted("promoCodes");
    setActiveStep("payment");
  }, [markStepCompleted]);

  const handlePaymentContinue = useCallback(() => {
    markStepCompleted("payment");
    setActiveStep("contract");
  }, [markStepCompleted]);

  const uploadContract = useCallback(async (): Promise<string | null> => {
    if (!contractFile) return null;
    try {
      const presigned = await presignContract.mutateAsync({
        content_type: "application/pdf",
      });
      // fetch only rejects on network errors, not on 4xx/5xx — without this
      // check a rejected S3 PUT (e.g. a 403 signature mismatch) would still
      // resolve and we'd associate an s3_key whose file never landed.
      const uploadResponse = await fetch(presigned.upload_url, {
        method: "PUT",
        headers: { "Content-Type": presigned.content_type },
        body: contractFile,
      });
      if (!uploadResponse.ok) {
        throw new Error(`S3 contract upload failed: ${uploadResponse.status}`);
      }
      return presigned.s3_key;
    } catch {
      // Upload failed — let the affiliate be created without the contract.
      message.warning(AppStrings.NEW_AFFILIATE_CONTRACT_UPLOAD_FAILED);
      return null;
    }
  }, [contractFile, presignContract]);

  const handleAffiliateSubmit = useCallback(async () => {
    if (!isContactInfoComplete(contactInfo)) {
      message.error(AppStrings.COMPLETE_CONTACT_INFORMATION_BEFORE);
      setActiveStep("contact");
      return;
    }
    if (!isPamAccountValid(pamAccount)) {
      message.error(AppStrings.COMPLETE_PAM_ACCOUNT_BEFORE_ADDING);
      setActiveStep("pamAccount");
      return;
    }
    if (!isPaymentValid(payment)) {
      message.error(AppStrings.COMPLETE_PAYMENT_BEFORE_ADDING);
      setActiveStep("payment");
      return;
    }

    let contractKey: string | null = null;
    try {
      contractKey = await uploadContract();

      const payload: CreateAffiliateRequest = {
        contact: mapContactToPayload(contactInfo),
        portal_view_config: mapPortalViewConfigToPayload(pamAccount),
        plan: mapPaymentToPayload(payment),
        promo_codes: mapPromoCodesToPayload(promoCodes),
        contract_s3_key: contractKey,
      };
      const result = await createAffiliate.mutateAsync(payload);
      setConfirmation(result);
      setCompletedSteps(new Set(NEW_AFFILIATE_STEPS.map((s) => s.id)));
      setActiveStep("confirmation");
    } catch (err) {
      const envelope = (err as {
        response?: { data?: { message?: string; error_code?: string } };
      })?.response?.data;
      message.error(
        envelope?.message || AppStrings.NEW_AFFILIATE_CREATE_FAILED,
      );
    }
  }, [
    contactInfo,
    pamAccount,
    payment,
    promoCodes,
    uploadContract,
    createAffiliate,
  ]);

  const renderActiveStep = (): ReactNode => {
    switch (activeStep) {
      case "contact":
        return (
          <ContactInformation
            initialValues={contactInfo}
            onValuesChange={setContactInfo}
            onSubmit={handleContactInfoSubmit}
          />
        );
      case "pamAccount":
        return (
          <AffiliatePamAccount
            initialValues={pamAccount}
            onValuesChange={setPamAccount}
            onContinue={handlePamAccountContinue}
          />
        );
      case "promoCodes":
        return (
          <PromoCodes
            promoCodes={promoCodes}
            onCreatePromoCode={handleCreatePromoCode}
            onEditPromoCode={handleEditPromoCode}
            onContinue={handlePromoCodesContinue}
          />
        );
      case "payment":
        return (
          <Payment
            initialValues={payment}
            onValuesChange={setPayment}
            onContinue={handlePaymentContinue}
          />
        );
      case "contract":
        return (
          <Contract
            file={contractFile}
            onChange={setContractFile}
            onSubmit={handleAffiliateSubmit}
            submitting={isSubmitting}
          />
        );
      case "confirmation":
        return confirmation ? (
          <Confirmation
            response={confirmation}
            recipientEmail={contactInfo.email}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <>
      <div className={styles.page} data-testid="new-affiliate">
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={AppStrings.BACK_TO_AFFILIATES_LIST}
            onClick={handleBack}
            data-testid="new-affiliate-back"
          >
            <LeftOutlined className={styles.backButtonIcon} />
          </button>
        </div>

        <div className={styles.content}>
          <main className={styles.wizardCard}>
            <WizardStepper
              steps={NEW_AFFILIATE_STEPS}
              activeStepId={activeStep}
              completedStepIds={Array.from(completedSteps)}
              onStepClick={handleStepClick}
              locked={activeStep === "confirmation"}
              idPrefix="new-affiliate-step"
              ariaLabel={AppStrings.NEW_AFFILIATE}
            />

            <div
              className={styles.stepPanel}
              role="tabpanel"
              id={`new-affiliate-step-panel-${activeStep}`}
              aria-labelledby={`new-affiliate-step-${activeStep}`}
              data-testid={`new-affiliate-step-panel-${activeStep}`}
            >
              {renderActiveStep()}
            </div>
          </main>

          <aside
            className={styles.summary}
            aria-label={AppStrings.NEW_AFFILIATE_SUMMARY_ARIA_LABEL}
            data-testid="new-affiliate-summary"
          >
            <section className={styles.summarySection}>
              <h3 className={styles.summaryTitle}>
                {AppStrings.PERSONAL_INFO}
              </h3>
              <dl className={styles.summaryList}>
                <div className={styles.summaryRow}>
                  <dt className={styles.summaryLabel}>
                    {AppStrings.NAME}:
                  </dt>
                  <dd
                    className={styles.summaryValue}
                    data-testid="new-affiliate-summary-name"
                  >
                    {contactInfo.name?.trim() || EMPTY_VALUE}
                  </dd>
                </div>
                <div className={styles.summaryRow}>
                  <dt className={styles.summaryLabel}>
                    {AppStrings.AFFILIATE_CONTACT}:
                  </dt>
                  <dd
                    className={styles.summaryValue}
                    data-testid="new-affiliate-summary-contact"
                  >
                    {contactInfo.email?.trim() || EMPTY_VALUE}
                  </dd>
                </div>
              </dl>
            </section>

            <section className={styles.summarySection}>
              <h3 className={styles.summaryTitle}>
                {AppStrings.NEW_AFFILIATE_PROMO_CODES}
              </h3>
              <dl className={styles.summaryList}>
                {promoCodes.length === 0 ? (
                  <div className={styles.summaryRow}>
                    <dt className={styles.summaryLabel}>{EMPTY_VALUE}</dt>
                    <dd className={styles.summaryValue}>{EMPTY_VALUE}</dd>
                  </div>
                ) : (
                  promoCodes.map((promoCode, idx) => (
                    <div
                      // The list is parent-owned and edits replace items in
                      // place — index as key is safe here.
                      key={idx + 1}
                      className={styles.summaryRow}
                      data-testid={`new-affiliate-summary-promo-code-${idx + 1}`}
                    >
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.NEW_AFFILIATE_PROMO_CODE_LABEL} ${
                          idx + 1
                        }:`}
                      </dt>
                      <dd className={styles.summaryValue}>
                        <span>{buildPromoCodeSlug(promoCode.name)}</span>
                        {(promoCode.deepLinkUrl || deepLinkBaseUrl) && (
                          <span className={styles.summarySubValue}>
                            {promoCode.deepLinkUrl ||
                              buildAffiliateDeepLinkUrl(
                                promoCode.name,
                                deepLinkBaseUrl,
                              )}
                          </span>
                        )}
                      </dd>
                    </div>
                  ))
                )}
              </dl>
            </section>

            <section
              className={styles.summarySection}
              data-testid="new-affiliate-summary-payment"
            >
              <h3 className={styles.summaryTitle}>
                {AppStrings.AFFILIATE_PAYMENT}
              </h3>
              <dl className={styles.summaryList}>
                {payment.type === "cpa" ? (
                  <>
                    <div className={styles.summaryRow}>
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.TYPE}:`}
                      </dt>
                      <dd
                        className={styles.summaryValue}
                        data-testid="new-affiliate-summary-payment-type"
                      >
                        {AppStrings.AFFILIATE_PAYMENT_CPA}
                      </dd>
                    </div>
                    <div className={styles.summaryRow}>
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.ACTION}:`}
                      </dt>
                      <dd
                        className={styles.summaryValue}
                        data-testid="new-affiliate-summary-payment-cpa-action"
                      >
                        {formatCpaActionType(payment.cpa?.actionType)}
                      </dd>
                    </div>
                    <div className={styles.summaryRow}>
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.PER_ACTION}:`}
                      </dt>
                      <dd
                        className={styles.summaryValue}
                        data-testid="new-affiliate-summary-payment-cpa-amount"
                      >
                        {formatAmount(payment.cpa?.paymentPerAction, currencySymbol)}
                      </dd>
                    </div>
                  </>
                ) : payment.type === "rev_share" ? (
                  <>
                    <div className={styles.summaryRow}>
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.TYPE}:`}
                      </dt>
                      <dd
                        className={styles.summaryValue}
                        data-testid="new-affiliate-summary-payment-type"
                      >
                        {AppStrings.AFFILIATE_PAYMENT_REV_SHARE}
                      </dd>
                    </div>
                    <div className={styles.summaryRow}>
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.SOURCE}:`}
                      </dt>
                      <dd
                        className={styles.summaryValue}
                        data-testid="new-affiliate-summary-payment-rev-share-source"
                      >
                        {formatRevenueSource(payment.revShare?.revenueSource)}
                      </dd>
                    </div>
                    {/*
                      Both Sportsbook and Casino rows always render under
                      Rev Share — the Payment step collects all four
                      monetary thresholds regardless of the chosen
                      revenue source, so the summary mirrors that
                      contract instead of hiding rows by source.
                    */}
                    <div
                      className={styles.summaryRow}
                      data-testid="new-affiliate-summary-payment-rev-share-sportsbook"
                    >
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.SPORTSBOOK}:`}
                      </dt>
                      <dd className={styles.summaryValue}>
                        <span>
                          {formatAmount(
                            payment.revShare?.sportsbook?.minProfit,
                            currencySymbol,
                          )}
                        </span>
                        <span className={styles.summarySubValue}>
                          {formatPct(payment.revShare?.sportsbook?.revenuePct)}
                        </span>
                      </dd>
                    </div>
                    <div
                      className={styles.summaryRow}
                      data-testid="new-affiliate-summary-payment-rev-share-casino"
                    >
                      <dt className={styles.summaryLabel}>
                        {`${AppStrings.CASINO}:`}
                      </dt>
                      <dd className={styles.summaryValue}>
                        <span>
                          {formatAmount(
                            payment.revShare?.casino?.minProfit,
                            currencySymbol,
                          )}
                        </span>
                        <span className={styles.summarySubValue}>
                          {formatPct(payment.revShare?.casino?.revenuePct)}
                        </span>
                      </dd>
                    </div>
                  </>
                ) : (
                  <div className={styles.summaryRow}>
                    <dt className={styles.summaryLabel}>{EMPTY_VALUE}</dt>
                    <dd className={styles.summaryValue}>{EMPTY_VALUE}</dd>
                  </div>
                )}
              </dl>
            </section>

            <section className={styles.summarySection}>
              <h3 className={styles.summaryTitle}>
                {AppStrings.AFFILIATE_CONTRACT}
              </h3>
              <dl className={styles.summaryList}>
                <div className={styles.summaryRow}>
                  <dt className={styles.summaryLabel}>
                    {AppStrings.AFFILIATE_CONTRACT_FILE}:
                  </dt>
                  <dd
                    className={styles.summaryValue}
                    data-testid="new-affiliate-summary-contract-file"
                  >
                    {contractFile?.name || EMPTY_VALUE}
                  </dd>
                </div>
              </dl>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
};

export default NewAffiliate;
